/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import * as yup from 'yup';

import { WebChatLayoutMode } from '../../../ia/components/preview/util';
import {
  BOT_NAME_MAX_LENGTH,
  CONTACT_LABEL_MAX_LENGTH,
  CONTACT_OPTIONS,
  GREETING_MAX_LENGTH,
  LAUNCHER_DESKTOP_TITLE_MAX_LENGTH,
  LAUNCHER_MOBILE_TITLE_MAX_LENGTH,
  STARTERS_MAX_LENGTH,
  COLOR_REGEX,
  NO_ONLY_SPACES_REGEX,
  CARBON_THEME_OPTIONS,
  HOME_SCREEN_BACKGROUND_OPTIONS,
  WEB_CHAT_SIZE_REGEX
} from '../../util/validation/constants';
import { Defaults, Validation } from './messages';

export const settingsSchema = yup.object().shape({
  private: yup.object().shape({
    authorization: yup.object().shape({
      is_on: yup.boolean().default(false),
      public_key: yup
        .string()
        .when('is_on', {
          is: on => on === true,
          then: yup.string().required(Validation.publicKeyRequired)
        })
        .nullable()
        .default(undefined),
      encryption: yup
        .object()
        .shape({
          public_key: yup.object().shape({
            key: yup.string(),
            created: yup.string()
          })
        })
        .nullable()
        .default(undefined)
    })
  }),
  accountOwnerDomain: yup.string().default(''),
  alternate_suggestions: yup.object().shape({
    is_on: yup.boolean().default(true),
    contact_option: yup.string().default(CONTACT_OPTIONS.ALWAYS),
    contact_label: yup
      .string()
      .trim()
      .required(Validation.contactLabelRequired)
      .max(CONTACT_LABEL_MAX_LENGTH, Validation.contactLabelMaxLength)
      .default(Defaults.contactLabel.defaultMessage)
  }),
  botAvatarURL: yup.string().ensure().default(''),
  botName: yup
    .string()
    .trim()
    .required(Validation.botNameRequired)
    .max(BOT_NAME_MAX_LENGTH, Validation.botNameMaxLength)
    .default(Defaults.botName.defaultMessage),
  branding_config: yup.object().shape({
    is_on: yup.boolean().default(true)
  }),
  css_variables: yup
    .object()
    .shape({
      'BASE-accent-color': yup
        .string()
        .matches(COLOR_REGEX, {
          message: Validation.colorPicker,
          excludeEmptyString: false
        })
        .default(''),
      'BASE-primary-color': yup
        .string()
        .matches(COLOR_REGEX, {
          message: Validation.colorPicker,
          excludeEmptyString: false
        })
        .default(''),
      'BASE-secondary-color': yup
        .string()
        .matches(COLOR_REGEX, {
          message: Validation.colorPicker,
          excludeEmptyString: false
        })
        .default(''),
      'BASE-width': yup
        .string()
        .matches(WEB_CHAT_SIZE_REGEX, {
          message: Validation.sizePixelValue,
          excludeEmptyString: false
        })
        .default(''),
      'BASE-height': yup
        .string()
        .matches(WEB_CHAT_SIZE_REGEX, {
          message: Validation.sizePixelValue,
          excludeEmptyString: false
        })
        .default('')
    })
    .required(),
  launcher_config: yup.object().shape({
    mobile: yup.object().shape({
      title: yup
        .string()
        .max(
          LAUNCHER_MOBILE_TITLE_MAX_LENGTH,
          Validation.launcherMobileTitleMaxLength
        )
        .matches(NO_ONLY_SPACES_REGEX, {
          message: Validation.mobileLauncherGreetingRequired,
          excludeEmptyString: true
        })
        .default('')
    }),
    desktop: yup.object().shape({
      title: yup
        .string()
        .max(
          LAUNCHER_DESKTOP_TITLE_MAX_LENGTH,
          Validation.launcherDesktopTitleMaxLength
        )
        .matches(NO_ONLY_SPACES_REGEX, {
          message: Validation.desktopLauncherGreetingRequired,
          excludeEmptyString: true
        })
        .default('')
    })
  }),
  home_screen: yup.object().shape({
    is_on: yup.boolean().default(true),
    greeting: yup
      .string()
      .trim()
      .required(Validation.greetingRequired)
      .max(GREETING_MAX_LENGTH, Validation.greetingMaxLength)
      .default(Defaults.greeting.defaultMessage),
    starters: yup.object().shape({
      is_on: yup.boolean().default(true),
      buttons: yup
        .array()
        .of(
          yup.object({
            label: yup
              .string()
              .trim()
              .required(Validation.startersRequired)
              .max(STARTERS_MAX_LENGTH, Validation.startersMaxLength),
            value: yup.string().default('')
          })
        )
        .default([
          { label: Defaults.starter0.defaultMessage, value: '' },
          { label: Defaults.starter1.defaultMessage, value: '' },
          { label: Defaults.starter2.defaultMessage, value: '' }
        ])
    }),
    background: yup.string().default(HOME_SCREEN_BACKGROUND_OPTIONS.NONE)
  }),
  plan: yup.string().default(''),
  preview: yup
    .object()
    .optional()
    .shape({
      layout: yup.object().shape({
        mode: yup
          .mixed<WebChatLayoutMode>()
          .oneOf(Object.values(WebChatLayoutMode))
          .default(WebChatLayoutMode.DEFAULT)
      })
    }),
  recentEditorDomain: yup.string().default(''),
  service_desk: yup.object().shape({
    is_on: yup.boolean().default(false),
    integration_id: yup.string().when('is_on', {
      is: on => on === true,
      then: yup.string().required(),
      otherwise: yup.string().default('')
    }),
    integration_type: yup.string().when('is_on', {
      is: on => on === true,
      then: yup.string().required(),
      otherwise: yup.string().default('')
    })
  }),
  streaming: yup.object().shape({
    is_on: yup.boolean().default(false)
  }),
  theme_config: yup.object().shape({
    carbon_theme: yup.string().default(CARBON_THEME_OPTIONS.LIGHT),
    use_ai_theme: yup.boolean().default(false)
  })
});

export const FIELDS = {
  ALTERNATE_SUGGESTIONS: {
    IS_ON: 'alternate_suggestions.is_on',
    CONTACT_LABEL: 'alternate_suggestions.contact_label',
    CONTACT_OPTION: 'alternate_suggestions.contact_option'
  },
  AUTHORIZATION: {
    CLIENT_PUBLIC_KEY: 'private.authorization.public_key',
    CREATED: 'private.authorization.encryption.public_key.created',
    IBM_PUBLIC_KEY: 'private.authorization.encryption.public_key.key',
    IS_ON: 'private.authorization.is_on'
  },
  BOT_AVATAR_URL: 'botAvatarURL',
  BOT_NAME: 'botName',
  BRANDING_CONFIG: 'branding_config',
  BRANDING_CONFIG_IS_ON: 'branding_config.is_on',
  CSS: {
    ACCENT_COLOR: 'css_variables.BASE-accent-color',
    PRIMARY_COLOR: 'css_variables.BASE-primary-color',
    SECONDARY_COLOR: 'css_variables.BASE-secondary-color',
    WIDTH: 'css_variables.BASE-width',
    HEIGHT: 'css_variables.BASE-height'
  },
  HOME_SCREEN: {
    IS_ON: 'home_screen.is_on',
    GREETING: 'home_screen.greeting',
    STARTERS: {
      IS_ON: 'home_screen.starters.is_on',
      BUTTONS: 'home_screen.starters.buttons',
      GET_BUTTON_LABEL: (index: number) =>
        `home_screen.starters.buttons.${index}.label`
    },
    BACKGROUND: 'home_screen.background'
  },
  LAUNCHER_DESKTOP_TITLE: 'launcher_config.desktop.title',
  LAUNCHER_MOBILE_TITLE: 'launcher_config.mobile.title',
  PREVIEW: {
    LAYOUT: {
      MODE: 'preview.layout.mode'
    }
  },
  SERVICE_DESK: {
    IS_ON: 'service_desk.is_on',
    INTEGRATION_ID: 'service_desk.integration_id',
    INTEGRATION_TYPE: 'service_desk.integration_type'
  },
  STREAMING: {
    IS_ON: 'streaming.is_on'
  },
  THEME_CONFIG: {
    CARBON_THEME: 'theme_config.carbon_theme',
    USE_AI_THEME: 'theme_config.use_ai_theme'
  }
};

export const SUBSCRIBE_PAYLOAD_TRANSFORM_ONLY_GENERATED_KEY = {
  [FIELDS.AUTHORIZATION.IBM_PUBLIC_KEY]:
    'private.authorization.encryption.public_key.key',
  [FIELDS.AUTHORIZATION.CREATED]:
    'private.authorization.encryption.public_key.created'
};

export const SUBSCRIBE_PAYLOAD_TRANSFORM = {
  ...SUBSCRIBE_PAYLOAD_TRANSFORM_ONLY_GENERATED_KEY,
  [FIELDS.AUTHORIZATION.CLIENT_PUBLIC_KEY]: 'private.authorization.public_key',
  [FIELDS.AUTHORIZATION.IS_ON]: 'private.authorization.is_on'
};
